import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { LOGO_URL } from "../i18n/translations";

const FlagPT = ({ active }) => (
  <span
    className={`inline-flex items-center justify-center w-6 h-6 rounded-full overflow-hidden border ${
      active ? "border-[#1C1C1C]" : "border-transparent opacity-60"
    } transition`}
    aria-label="Português"
  >
    <svg viewBox="0 0 6 4" className="w-full h-full">
      <rect width="2.4" height="4" fill="#006600" />
      <rect x="2.4" width="3.6" height="4" fill="#FF0000" />
      <circle cx="2.4" cy="2" r="0.75" fill="#FFD700" />
    </svg>
  </span>
);

const FlagEN = ({ active }) => (
  <span
    className={`inline-flex items-center justify-center w-6 h-6 rounded-full overflow-hidden border ${
      active ? "border-[#1C1C1C]" : "border-transparent opacity-60"
    } transition`}
    aria-label="English"
  >
    <svg viewBox="0 0 60 30" className="w-full h-full">
      <clipPath id="s"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
      <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#s)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  </span>
);

const LanguageSwitcher = ({ darkMode }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      className="flex items-center gap-1 pl-3 ml-2 border-l border-[#E5E5E5]/60"
      data-testid="language-switcher"
    >
      <button
        type="button"
        onClick={() => setLang("pt")}
        className="p-1 hover:opacity-100 transition"
        data-testid="lang-pt-btn"
        aria-pressed={lang === "pt"}
      >
        <FlagPT active={lang === "pt"} />
      </button>
      <button
        type="button"
        onClick={() => setLang("en")}
        className="p-1 hover:opacity-100 transition"
        data-testid="lang-en-btn"
        aria-pressed={lang === "en"}
      >
        <FlagEN active={lang === "en"} />
      </button>
    </div>
  );
};

const Header = ({ darkMode = false }) => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: "/sociedade", label: t.nav.sociedade, id: "sociedade" },
    { to: "/projetos", label: t.nav.projetos, id: "projetos" },
    { to: "/primewash", label: t.nav.primewash, id: "primewash" },
    { to: "/contactos", label: t.nav.contactos, id: "contactos" },
  ];

  const base = darkMode
    ? "bg-[#0A0E17]/95 border-white/10"
    : "bg-white border-[#E5E5E5]";

  const linkColor = darkMode ? "text-white/80 hover:text-white" : "text-[#1C1C1C]/80 hover:text-[#1C1C1C]";
  const activeColor = darkMode ? "text-white" : "text-[#1C1C1C]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b transition-all duration-300 ${base}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Bola8 AzorHomes"
            className={`h-12 md:h-14 w-auto ${darkMode ? "brightness-0 invert" : ""}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10" data-testid="main-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                `text-sm uppercase tracking-[0.18em] font-medium transition-colors duration-300 ${
                  isActive ? activeColor : linkColor
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <LanguageSwitcher darkMode={darkMode} />
          <Link
            to="/contactos"
            data-testid="nav-cta-btn"
            className={`ml-2 px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 ${
              darkMode
                ? "bg-[#0066FF] text-white hover:bg-[#0052CC]"
                : "bg-[#1C1C1C] text-white hover:bg-black"
            }`}
          >
            {t.nav.cta}
          </Link>
        </nav>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? (
            <X className={darkMode ? "text-white" : "text-[#1C1C1C]"} />
          ) : (
            <Menu className={darkMode ? "text-white" : "text-[#1C1C1C]"} />
          )}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div
          className={`lg:hidden border-t ${
            darkMode ? "bg-[#0A0E17] border-white/10" : "bg-white border-[#E5E5E5]"
          }`}
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                data-testid={`nav-mobile-${item.id}`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-[0.18em] ${
                    isActive ? activeColor : linkColor
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-2">
              <LanguageSwitcher darkMode={darkMode} />
            </div>
            <Link
              to="/contactos"
              data-testid="nav-mobile-cta-btn"
              className={`inline-block text-center mt-2 px-6 py-4 text-xs uppercase tracking-[0.2em] ${
                darkMode ? "bg-[#0066FF] text-white" : "bg-[#1C1C1C] text-white"
              }`}
            >
              {t.nav.cta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
